type UomOption = {
  productUnitOfMeasureId: number;
  uomTypeName: string;
  conversionFactor: number;
  eaches: number;
};

export const EACH_UOM: UomOption = { productUnitOfMeasureId: 0, uomTypeName: 'Each', conversionFactor: 1, eaches: 1 };

type HierarchyItem = { id: number; label: string; eaches: number; parentId: number | null };

// Walks the parentId chain downward (Pallet→Case→Pack) to build the label for `item`.
// parentId points from larger to smaller unit, so following it yields the full breakdown path.
export const buildUomHierarchy = (item: HierarchyItem, allItems: HierarchyItem[]): string => {
  const byId = new Map(allItems.map((o) => [o.id, o]));
  const chain: HierarchyItem[] = [item];
  const visited = new Set<number>([item.id]);
  let current = item;
  while (current.parentId != null) {
    if (visited.has(current.parentId)) break;
    const next = byId.get(current.parentId);
    if (!next) break;
    if (next.eaches >= current.eaches) break;
    chain.push(next);
    visited.add(next.id);
    current = next;
  }

  const leaf = chain[chain.length - 1];
  if (leaf.eaches > 1) {
    const eachItem = allItems.find((o) => o.id === EACH_UOM.productUnitOfMeasureId);
    if (eachItem) chain.push(eachItem);
  }

  if (chain.length === 1) return item.label;

  const parts = chain.slice(1).map((c) => `${item.eaches / c.eaches} x ${c.label}`);
  return `1 ${item.label} = ${parts.join(' = ')}`;
};

type RawUomNode = { productUnitOfMeasureId: number; conversionFactor: number; parentProductUnitOfMeasureId?: number | null };

type UomDropdownOption = { id: number; label: string; value: string; eaches: number; parentId: number | null };

export type NamedUomNode = RawUomNode & { uomTypeName: string };

export const buildUomDropdownOptions = (rawUoms: NamedUomNode[]): UomDropdownOption[] => {
  const nonEach = rawUoms.filter((u) => u.productUnitOfMeasureId !== 0);
  const options: UomDropdownOption[] = [
    { id: 0, label: EACH_UOM.uomTypeName, value: '0', eaches: 1, parentId: null },
    ...nonEach.map((u) => ({
      id: u.productUnitOfMeasureId,
      label: u.uomTypeName,
      value: String(u.productUnitOfMeasureId),
      eaches: computeAbsoluteEaches(u.productUnitOfMeasureId, nonEach),
      parentId: u.parentProductUnitOfMeasureId ?? null,
    })),
  ];
  return options.sort((a, b) => a.eaches - b.eaches);
};

export const computeAbsoluteEaches = (uomId: number, rawUoms: RawUomNode[]): number => {
  const byId = new Map(rawUoms.map((u) => [u.productUnitOfMeasureId, u]));
  const target = byId.get(uomId);
  if (!target) return 1;

  let eaches = 1;
  let current = target;
  const visited = new Set<number>();
  while (true) {
    if (visited.has(current.productUnitOfMeasureId)) break;
    visited.add(current.productUnitOfMeasureId);
    eaches *= current.conversionFactor;
    if (current.parentProductUnitOfMeasureId == null) break;
    const parent = byId.get(current.parentProductUnitOfMeasureId);
    if (!parent) break;
    current = parent;
  }
  return eaches;
};

export const UOM_TYPE_NAME = {
  Each: 'Each',
  Pack: 'Pack',
  Case: 'Case',
  Pallet: 'Pallet',
} as const;
